import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Divider,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button
} from '@mui/material';
import apiService from '../../services/apiService';
import { useToast } from '../common/ToastProvider';

function UserCronJobsCard({ userId }) {
  const { showSuccess, showError } = useToast();
  const [cronJobs, setCronJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadingSwitches, setLoadingSwitches] = useState({});
  const [confirmationModal, setConfirmationModal] = useState({
    open: false,
    cronJobId: null,
    newValue: null,
    cronJobName: null
  });

  const fetchCronJobs = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await apiService.fetchUserAutoDetectReporter(userId);
      
      if (result.success) {
        const autoDetectReporter = result.data.auto_detect_reporter;
        setCronJobs([
          {
            id: 'auto-detect-reporter',
            name: 'Auto Detect Reporter',
            description: 'Automatically detects and reports issues based on configured time period',
            enabled: autoDetectReporter.enabled,
            timePeriod: autoDetectReporter.time_period,
            type: 'auto-detect-reporter'
          }
        ]);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to load cron jobs');
      console.error('Failed to fetch cron jobs:', err);
    }
    
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    fetchCronJobs();
  }, [fetchCronJobs]);

  const handleCronJobToggle = (cronJobId, newValue, cronJobName) => {
    setConfirmationModal({
      open: true,
      cronJobId,
      newValue,
      cronJobName
    });
  };

  const handleConfirmToggle = async () => {
    const { cronJobId, newValue } = confirmationModal;
    
    // Set loading state for this specific cron job
    setLoadingSwitches(prev => ({ ...prev, [cronJobId]: true }));
    
    // Close modal immediately
    setConfirmationModal({ open: false, cronJobId: null, newValue: null, cronJobName: null });
    
    // Optimistically update the UI
    setCronJobs(prev => 
      prev.map(job => 
        job.id === cronJobId 
          ? { ...job, enabled: newValue }
          : job
      )
    );

    try {
      let result;
      
      if (cronJobId === 'auto-detect-reporter') {
        result = await apiService.updateUserAutoDetectReporterEnabled(userId, newValue);
      }
      
      if (result && result.success) {
        console.log('Cron job updated successfully:', result.data);
        showSuccess(`Cron job "${cronJobId}" ${newValue ? 'enabled' : 'disabled'} successfully`, 'Success');
      } else if (result) {
        console.error('Failed to update cron job:', result.error);
        // Revert the optimistic update on error
        setCronJobs(prev => 
          prev.map(job => 
            job.id === cronJobId 
              ? { ...job, enabled: !newValue }
              : job
          )
        );
        showError(`Failed to update cron job: ${result.error}`, 'Error');
      }
    } catch (err) {
      console.error('Error updating cron job:', err);
      // Revert the optimistic update on error
      setCronJobs(prev => 
        prev.map(job => 
          job.id === cronJobId 
            ? { ...job, enabled: !newValue }
            : job
        )
      );
      showError('Failed to update cron job. Please try again.', 'Error');
    } finally {
      // Clear loading state for this specific cron job
      setLoadingSwitches(prev => ({ ...prev, [cronJobId]: false }));
    }
  };

  const handleCancelToggle = () => {
    setConfirmationModal({ open: false, cronJobId: null, newValue: null, cronJobName: null });
  };

  const getTimePeriodColor = (timePeriod) => {
    switch (timePeriod) {
      case 'day':
        return 'success';
      case 'week':
        return 'primary';
      case 'month':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatTimePeriod = (timePeriod) => {
    switch (timePeriod) {
      case 'day':
        return 'Daily';
      case 'week':
        return 'Weekly';
      case 'month':
        return 'Monthly';
      default:
        return timePeriod;
    }
  };

  if (loading) {
    return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h6" gutterBottom>
            ⏰ Cron Jobs
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3, flexGrow: 1 }}>
            <CircularProgress size={40} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h6" gutterBottom>
            ⏰ Cron Jobs
          </Typography>
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h6" gutterBottom>
            ⏰ Cron Jobs
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <Box sx={{ flexGrow: 1 }}>
            {cronJobs.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  No cron jobs configured for this user
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2} justifyContent="center">
                {cronJobs.map((job) => (
                  <Grid item xs={12} sm={8} md={6} key={job.id}>
                    <Box sx={{ 
                      border: '1px solid', 
                      borderColor: 'divider', 
                      borderRadius: 1, 
                      p: 2,
                      backgroundColor: 'background.paper'
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body1" fontWeight="medium" sx={{ mb: 0.5 }}>
                            {job.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {job.description}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip 
                              label={formatTimePeriod(job.timePeriod)} 
                              color={getTimePeriodColor(job.timePeriod)} 
                              size="small" 
                              variant="outlined"
                            />
                            <Chip 
                              label={job.enabled ? 'Active' : 'Inactive'} 
                              color={job.enabled ? 'success' : 'default'} 
                              size="small" 
                              variant="outlined"
                            />
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={job.enabled}
                                onChange={(e) => handleCronJobToggle(job.id, e.target.checked, job.name)}
                                color="primary"
                                disabled={loadingSwitches[job.id]}
                                sx={{
                                  '& .MuiSwitch-thumb': {
                                    opacity: loadingSwitches[job.id] ? 0.6 : 1,
                                  },
                                  '& .MuiSwitch-track': {
                                    opacity: loadingSwitches[job.id] ? 0.6 : 1,
                                  }
                                }}
                              />
                            }
                            label=""
                          />
                          {loadingSwitches[job.id] && (
                            <CircularProgress size={16} />
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Confirmation Modal */}
      <Dialog
        open={confirmationModal.open}
        onClose={handleCancelToggle}
        aria-labelledby="cron-job-dialog-title"
        aria-describedby="cron-job-dialog-description"
      >
        <DialogTitle id="cron-job-dialog-title">
          {confirmationModal.newValue ? 'Enable Cron Job' : 'Disable Cron Job'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="cron-job-dialog-description">
            Are you sure you want to {confirmationModal.newValue ? 'enable' : 'disable'} the cron job <strong>"{confirmationModal.cronJobName}"</strong>?
            {confirmationModal.newValue ? 
              ' This will activate the automated reporting functionality.' : 
              ' This will deactivate the automated reporting functionality.'
            }
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelToggle} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmToggle} 
            color="primary" 
            variant="contained"
            disabled={loadingSwitches[confirmationModal.cronJobId]}
            startIcon={loadingSwitches[confirmationModal.cronJobId] ? <CircularProgress size={16} /> : null}
          >
            {loadingSwitches[confirmationModal.cronJobId] ? 'Updating...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default UserCronJobsCard; 